import { defineComponent, createApp, reactive } from 'vue';

import { fetcher, FetchResult } from '@/common/fetcher';
import { RoleChoice, UserType } from '@/common/interfaces';

import { useAccountStore } from '@/account/accountStore';
import { storeToRefs } from 'pinia';
import { PageEventBus } from '@/app/PageEventBus';

declare module '@vue/runtime-core' {
	interface ComponentCustomProperties {
		$auth: {
			isAdmin: boolean;
			user: UserType;
			setItem: (key: string, value: string) => boolean;
			getItem: (key: string) => string | null;
			removeItem: (key: string) => void;
			clearStorage: () => void;
			hasOneOfRoles: (roles: Array<RoleChoice>) => boolean;
			isInternalMember: boolean;
		};
	}
}

class AuthPluginClass {
	loaded = false;
	error = null;
	isAdmin = false;
	userId = null as String | null;
	storageQueue = [] as Array<Array<string>>;

	created() {
		const userInitial = document.getElementById('user-initial');

		const accountStore = useAccountStore();

		if (userInitial !== null) {
			const data = JSON.parse(userInitial.textContent!) as UserType;
			accountStore.setAccount(data);
			this.error = null;
			this.isAdmin = this.user!.role === RoleChoice.ADMIN;
			this.loaded = true;
		} else {
			this.reloadUser();
		}

		PageEventBus.on('auth:reload-user', () => { this.reloadUser(); });
	}

	destroyed(): void {

	}

	userLoaded(result: FetchResult): void {
		const accountStore = useAccountStore();
		if (result.response.ok && result.json.success) {
			const user = result.json.data as UserType;
			accountStore.setAccount(user);
			this.error = null;
			this.isAdmin = this.user!.role === RoleChoice.ADMIN;
		} else {
			console.log(result);
		}
		this.loaded = true;
	}

	userLoadFailed(result: FetchResult): void {
		console.log(result);
	}

	setItem(key: string, value: string): boolean {
		if (this.user === null) {
			this.storageQueue.push([key, value]);
			return false;
		}

		localStorage.setItem(`hus-${this.user.uuid}-${key}`, value);

		return true;
	}

	getItem(key: string): string | null {
		if (this.user === null) return null;

		return localStorage.getItem(`hus-${this.user.uuid}-${key}`);
	}

	removeItem(key: string): void {
		if (this.user === null) return;

		localStorage.removeItem(`hus-${this.user.uuid}-${key}`);
	}

	clearStorage(): void {
		for (let i = 0; i < localStorage.length; i++) {
			let key = localStorage.key(i);

			if (key?.startsWith('hus-')) {
				localStorage.removeItem(key);
			}
		}
	}

	hasOneOfRoles(roles: Array<RoleChoice>): boolean {
		if (this.user === null) {
			return false;
		}

		return roles.indexOf(this.user.role) > -1;
	}

	get isInternalMember(): boolean {
		return this.hasOneOfRoles([RoleChoice.ADMIN, RoleChoice.MEMBER]);
	}

	get user(): UserType | null {
		return useAccountStore().user;
	}

	async reloadUser(): Promise<UserType|null> {
		await fetcher.Get('/account/session/')
			.then(this.userLoaded.bind(this))
			.catch(this.userLoadFailed.bind(this));

		if (this.user !== null) {
			return Promise.resolve(this.user as UserType);
		} else {
			return Promise.reject(null);
		}
	}
};

export const AuthPlugin = {
	install(app: any, options: any) {
		const instance = reactive(new AuthPluginClass());
		app.config.globalProperties.$auth = instance;
		app.provide('$auth', instance);
	},
};
